// Live voice conversation ("voice mode") for any chat: you talk, it answers out loud, and the
// conversation keeps flowing into the chat as ordinary messages.
//
//   mic ──16 kHz PCM──▶ /api/morrow/voice/listen (Deepgram Flux: captions + end-of-turn)
//   end of your turn ──▶ onUtterance(text)  → the caller sends it as a normal chat message
//   the reply streams in (props.reply) ──sentence by sentence──▶ /api/morrow/voice/speak (Aura-2)
//   you start talking while it speaks ──▶ playback stops + onInterrupt() (barge-in)
import { useCallback, useEffect, useRef, useState } from "react";

import { apiUrl } from "../../apiBase";
import { getClerkToken } from "../../clerkTokenBridge";
import { int16ToFloat, PcmPlayer } from "./pcmPlayer";

// Hidden instructions ride after this marker; the chat shows only the text before it.
const DIRECTIVE_MARKER = "===MORROW_WORKFLOW_DIRECTIVE===";

/** What gets sent for something you said: your words, plus a hidden note so the reply works
 * out loud — short, plain, and a quick heads-up before any slow tool work. */
export function voiceTurnText(said: string, task?: string): string {
  const handoff = task
    ? `They've already been told out loud that you're on it. The task, as handed over: ${task}. `
    : "";
  return (
    `${said}\n\n${DIRECTIVE_MARKER}\n` +
    "(This message was spoken in a live voice conversation, and your reply will be read aloud. " +
    handoff +
    "Answer in one to three short, natural spoken sentences — no lists, tables, headings or " +
    "links. If you need tools, first say in a few words what you're about to do, then do it, " +
    "then give the answer briefly. Full detail can follow in the chat afterwards.)"
  );
}

// A new chat becomes a saved thread when its first message is sent, and the page swaps to the
// thread's own view. A spoken first message leaves this note so voice mode carries on there
// (and reads the reply out) instead of ending with the old view.
let carriedVoice: { sentAt: number; at: number } | null = null;

export function carryVoiceSession(sentAt: number): void {
  carriedVoice = { sentAt, at: Date.now() };
}

/** The send time of a spoken turn whose voice session should continue here, if any (once). */
export function takeCarriedVoiceSession(): number | null {
  const carried = carriedVoice;
  carriedVoice = null;
  return carried && Date.now() - carried.at < 60_000 ? carried.sentAt : null;
}

/** Messages recorded from voice mode (what you said, and the voice model's answers) carry this
 * id prefix, so they're never read out again as if they were the agent's reply. */
export const VOICE_MESSAGE_PREFIX = "voice-";

/** What to read out after a spoken turn: every reply the agent has written since your last
 * message (it can answer in several messages between tool steps), joined in order. */
export function voiceReplyFrom(
  messages: ReadonlyArray<{
    readonly id: string;
    readonly role: string;
    readonly text: string;
    readonly createdAt: string;
    readonly streaming?: boolean;
  }>,
  sentAt: number,
  working: boolean,
): VoiceReply | null {
  if (!sentAt) return null;
  let start = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]!;
    if (m.role === "user" && !m.id.startsWith(VOICE_MESSAGE_PREFIX)) {
      start = i;
      break;
    }
  }
  const turn = messages[start];
  if (!turn || (Date.parse(turn.createdAt) || 0) < sentAt - 5000) return null;
  const replies = messages
    .slice(start + 1)
    .filter((m) => m.role === "assistant" && !m.id.startsWith(VOICE_MESSAGE_PREFIX));
  if (replies.length === 0) return null;
  return {
    id: turn.id,
    text: replies
      .map((m) => visibleMessageText(m.text).trim())
      .filter(Boolean)
      .join("\n\n"),
    done: !working && !replies.some((m) => m.streaming),
  };
}

/** Did the mic just pick up our own voice? (Most of what it heard is in what's being said.) */
export function isEcho(heard: string, speaking: string): boolean {
  const heardWords = normWords(heard).split(" ").filter(Boolean);
  if (heardWords.length === 0 || !speaking) return false;
  const said = new Set(normWords(speaking).split(" "));
  const matched = heardWords.filter((w) => said.has(w)).length;
  return matched / heardWords.length >= 0.6;
}

/** The part of a message meant for people (everything before a hidden directive). */
export function visibleMessageText(text: string): string {
  const i = text.indexOf(DIRECTIVE_MARKER);
  return i === -1 ? text : text.slice(0, i).trimEnd();
}

export type VoiceStatus = "idle" | "connecting" | "listening" | "thinking" | "speaking" | "error";

export interface VoiceReply {
  /** The assistant message being spoken (its id keys what has already been read out). */
  readonly id: string;
  readonly text: string;
  /** True once the reply has finished streaming. */
  readonly done: boolean;
}

// Downsamples the mic to 16 kHz mono and posts ~50 ms Int16 frames plus a 0–1 level.
const PCM_WORKLET = `
class Pcm16k extends AudioWorkletProcessor {
  constructor() { super(); this.ratio = sampleRate / 16000; this.pos = 0; this.acc = 0; this.n = 0; this.buf = []; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) {
      this.acc += ch[i]; this.n += 1; this.pos += 1;
      if (this.pos >= this.ratio) { this.pos -= this.ratio; this.buf.push(this.acc / this.n); this.acc = 0; this.n = 0; }
    }
    if (this.buf.length >= 800) {
      const out = new Int16Array(this.buf.length); let sum = 0;
      for (let i = 0; i < this.buf.length; i++) {
        const v = Math.max(-1, Math.min(1, this.buf[i])); sum += v * v;
        out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
      }
      this.port.postMessage({ pcm: out.buffer, level: Math.min(1, Math.sqrt(sum / this.buf.length) * 4) }, [out.buffer]);
      this.buf = [];
    }
    return true;
  }
}
registerProcessor("pcm16k", Pcm16k);
`;

/** Turn chat markdown into something worth saying out loud. */
export function speakable(text: string): string {
  return text
    .replace(/```[\s\S]*?(```|$)/g, " I've put the code in the chat. ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, " the link in the chat ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/[*_~>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** The complete sentences in `text` after `from`, and where the next unread one starts. */
export function takeSentences(
  text: string,
  from: number,
  final: boolean,
  minChars = 40,
): { chunk: string; next: number } {
  const rest = text.slice(from);
  if (final) return { chunk: rest, next: text.length };
  // The last sentence boundary (., !, ? or a line break followed by whitespace) in the unread
  // part; wait for ~40 characters so tiny fragments aren't spoken one by one.
  const re = /[.!?](?=\s)|\n/g;
  let cut = -1;
  for (let m = re.exec(rest); m; m = re.exec(rest)) cut = m.index + 1;
  if (cut < minChars) return { chunk: "", next: from };
  return { chunk: rest.slice(0, cut), next: from + cut };
}

const normWords = (t: string) =>
  t
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/** `final` is `early` (ignoring case and punctuation) with at most its last word finished
 * ("…in simple w" → "…in simple words"): the live transcript often trails by a few letters,
 * which doesn't change the question. */
export function finishesWords(early: string, final: string): boolean {
  if (!normWords(early)) return false;
  const a = normWords(early).split(" ");
  const b = normWords(final).split(" ");
  if (a.length !== b.length) return false;
  const last = a.length - 1;
  return a.slice(0, last).join(" ") === b.slice(0, last).join(" ") && b[last]!.startsWith(a[last]!);
}

// Below this mic level (0–1) for QUIET_FRAMES frames (~50 ms each) you've likely stopped: start
// preparing the reply before the recogniser even calls the pause.
const QUIET_LEVEL = 0.05;
const QUIET_FRAMES = 4;

function listenUrl(token: string, keyterm: string | undefined): string {
  const http = apiUrl("/api/morrow/voice/listen");
  const abs = http.startsWith("http") ? http : `${window.location.origin}${http}`;
  const term = keyterm ? `&keyterm=${encodeURIComponent(keyterm)}` : "";
  return `${abs.replace(/^http/, "ws")}?clerkToken=${encodeURIComponent(token)}${term}`;
}

export function useVoiceConversation(props: {
  /** The reply to speak right now (latest assistant message after your last voice turn). */
  readonly reply: VoiceReply | null;
  /** A turn is in progress (used to know when the agent has finished). */
  readonly busy: boolean;
  /** Called with what you said once you finish speaking — send it as a chat message. */
  readonly onUtterance: (text: string) => void;
  /** Called when you talk over the reply — stop the running turn. */
  readonly onInterrupt: () => void;
  /** Aura-2 voice (e.g. "luna", "orion"). */
  readonly speaker?: string;
  /** A word to listen out for — the name you call it by. */
  readonly name?: string;
  /** Fast voice brain: an edge model answers right away (well under a second); anything that
   * needs real work is handed to the agent via onDelegate. Without it, every turn goes to the
   * agent (slow). */
  readonly brain?: {
    readonly persona: string;
    readonly history: () => ReadonlyArray<{
      readonly role: "user" | "assistant";
      readonly text: string;
    }>;
  };
  /** A finished spoken exchange with the voice brain — record it in the chat. */
  readonly onExchange?: (
    messages: ReadonlyArray<{ readonly role: "user" | "assistant"; readonly text: string }>,
  ) => void;
  /** The voice brain handed this off: send what you said to the agent (with the task), and
   * record the brain's short spoken acknowledgement after it. */
  readonly onDelegate?: (said: string, task: string, ack: string) => void;
}) {
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [level, setLevel] = useState(0);
  const [muted, setMuted] = useState(false);

  const ws = useRef<WebSocket | null>(null);
  const ctx = useRef<AudioContext | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const mutedRef = useRef(false);
  mutedRef.current = muted;
  const inputLevel = useRef(0);

  // Playback queue: each sentence is fetched as soon as it's ready and played in order.
  // Each queued sentence is a speech request already in flight; they play back to back.
  const queue = useRef<Array<Promise<Response | null>>>([]);
  const player = useRef<PcmPlayer | null>(null);
  const draining = useRef(false);
  const spoken = useRef<{ id: string; upTo: number }>({ id: "", upTo: 0 });
  const generation = useRef(0);
  const awaitingReply = useRef(false);

  const propsRef = useRef(props);
  propsRef.current = props;
  // A short spoken acknowledgement played right after you finish, so the agent never sits silent
  // while it works. Fetched once per conversation and reused.
  const fillers = useRef<Array<Float32Array<ArrayBuffer> | null>>([]);
  const fillerTimers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  // A finished turn waits briefly before it's sent: if you carry on ("Hey Atlas… what's new?"),
  // the pieces are joined into one message instead of two.
  const pendingSay = useRef<{ text: string; timer: ReturnType<typeof setTimeout> } | null>(null);

  type BrainRun = {
    said: string;
    text: string;
    upTo: number;
    confirmed: boolean;
    done: boolean;
    delegated: boolean;
    /** Recorded in the chat already. */
    committed: boolean;
    ac: AbortController;
    /** Speech that arrived while your turn wasn't confirmed yet — played the moment it is. */
    prepared: Array<Float32Array<ArrayBuffer>>;
    /** A dangling byte between audio chunks (PCM samples are 2 bytes). */
    carry: number | null;
  };
  const brainRun = useRef<BrainRun | null>(null);
  const eagerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What was said in voice this call (the brain's replies aren't chat messages).
  const voiceLog = useRef<Array<{ role: "user" | "assistant"; text: string }>>([]);

  const clearFillers = useCallback(() => {
    for (const t of fillerTimers.current) clearTimeout(t);
    fillerTimers.current = [];
  }, []);

  const stopSpeaking = useCallback(() => {
    clearFillers();
    brainRun.current?.ac.abort();
    brainRun.current = null;
    generation.current += 1;
    queue.current = [];
    player.current?.stop();
  }, [clearFillers]);

  /** Start generating speech for this text (raw PCM, streamed); the request is in flight
   * immediately, so the next sentence is ready by the time the current one ends. */
  const fetchSpeech = useCallback(async (text: string): Promise<Response | null> => {
    const token = await getClerkToken().catch(() => null);
    const res = await fetch(apiUrl("/api/morrow/voice/speak"), {
      method: "POST",
      headers: { "content-type": "application/json", ...(token ? { "x-clerk-token": token } : {}) },
      body: JSON.stringify({ text, speaker: propsRef.current.speaker, encoding: "linear16" }),
    }).catch(() => null);
    return res?.ok ? res : null;
  }, []);

  /** Record the short "still working" lines in the current voice, ready to play instantly. */
  const loadFillers = useCallback(() => {
    const set: Array<Float32Array<ArrayBuffer> | null> = [null, null, null];
    fillers.current = set;
    ["One moment.", "Still checking.", "Almost there."].forEach((line, i) => {
      void fetchSpeech(line).then(async (res) => {
        set[i] = res ? await PcmPlayer.samples(res) : null;
      });
    });
  }, [fetchSpeech]);

  const drain = useCallback(async () => {
    if (draining.current) return;
    draining.current = true;
    const gen = generation.current;
    const wanted = () => gen === generation.current;
    while (queue.current.length > 0 && wanted()) {
      const res = await queue.current.shift()!;
      if (!res || !wanted() || !player.current) continue;
      setStatus("speaking");
      await player.current.playStream(res, wanted);
    }
    await player.current?.idle(wanted);
    draining.current = false;
    // A barge-in can end this loop while newer sentences were queued: play those now.
    if (queue.current.length > 0) {
      void drainRef.current();
      return;
    }
    if (gen === generation.current && !awaitingReply.current) setStatus("listening");
  }, []);
  const drainRef = useRef(drain);
  drainRef.current = drain;

  /** Play the prepared fillers ("One moment." / "Still checking." / "Almost there.") at these
   * delays while we wait on the agent, unless something real is already playing. */
  const scheduleFillers = useCallback(
    (delays: number[], firstIndex: number) => {
      clearFillers();
      delays.forEach((delay, k) => {
        fillerTimers.current.push(
          setTimeout(() => {
            const samples = fillers.current[firstIndex + k];
            if (
              !samples ||
              !player.current ||
              !awaitingReply.current ||
              player.current.busy ||
              queue.current.length > 0
            ) {
              return;
            }
            player.current.play(samples);
          }, delay),
        );
      });
    },
    [clearFillers],
  );

  /** Put a spoken exchange into the chat (once): what you said and what it answered — or as
   * much of the answer as was spoken before you cut in. */
  const commitRun = useCallback((run: BrainRun) => {
    if (run.committed || !run.confirmed) return;
    const visible = run.text.replace(/\[\[[\s\S]*$/, "").trim();
    if (!run.said.trim() || !visible) return;
    run.committed = true;
    propsRef.current.onExchange?.([
      { role: "user", text: run.said },
      { role: "assistant", text: run.done ? visible : `${visible}…` },
    ]);
  }, []);

  /** Once the reply has finished and your turn is confirmed: log it, hand off any task, and go
   * back to listening when the audio has played out. */
  const finishBrain = useCallback(
    (run: BrainRun) => {
      if (brainRun.current !== run || !run.done || !run.confirmed) return;
      const visible = run.text.replace(/\[\[[\s\S]*$/, "").trim();
      if (visible) voiceLog.current.push({ role: "assistant", text: visible });
      const handoff = /\[\[DELEGATE:\s*([\s\S]*?)\]\]/.exec(run.text)?.[1]?.trim();
      if (handoff && !run.delegated) {
        run.delegated = true;
        run.committed = true;
        awaitingReply.current = true;
        propsRef.current.onDelegate?.(run.said, handoff, visible);
        scheduleFillers([15000, 35000], 1);
        return;
      }
      commitRun(run);
      const gen = generation.current;
      void player.current
        ?.idle(() => gen === generation.current)
        .then(() => {
          if (gen === generation.current && !awaitingReply.current) setStatus("listening");
        });
    },
    [commitRun, scheduleFillers],
  );

  /** Your turn is confirmed: play what's been prepared, and everything from now on, right away. */
  const confirmBrain = useCallback(
    (run: BrainRun) => {
      if (brainRun.current !== run) return;
      run.confirmed = true;
      if (run.prepared.length > 0 && player.current) {
        setStatus("speaking");
        for (const samples of run.prepared.splice(0)) player.current.play(samples);
      }
      finishBrain(run);
    },
    [finishBrain],
  );

  const onBrainAudio = useCallback((run: BrainRun, b64: string) => {
    if (brainRun.current !== run) return;
    let bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    if (run.carry !== null) {
      const merged = new Uint8Array(bytes.length + 1);
      merged[0] = run.carry;
      merged.set(bytes, 1);
      bytes = merged;
      run.carry = null;
    }
    if (bytes.length % 2 === 1) {
      run.carry = bytes[bytes.length - 1]!;
      bytes = bytes.subarray(0, bytes.length - 1);
    }
    const samples = int16ToFloat(bytes);
    if (run.confirmed && player.current) {
      setStatus("speaking");
      player.current.play(samples);
    } else {
      run.prepared.push(samples);
    }
  }, []);

  /** Ask the fast brain. `confirmed` = you've finished; unconfirmed runs (started on an eager
   * end-of-turn) prepare silently and only speak once your turn is confirmed. */
  const runBrain = useCallback(
    (said: string, confirmed: boolean) => {
      const brain = propsRef.current.brain;
      if (!brain) return;
      brainRun.current?.ac.abort();
      const run: BrainRun = {
        said,
        text: "",
        upTo: 0,
        confirmed,
        done: false,
        delegated: false,
        committed: false,
        ac: new AbortController(),
        prepared: [],
        carry: null,
      };
      brainRun.current = run;
      void (async () => {
        const token = await getClerkToken().catch(() => null);
        const res = await fetch(apiUrl("/api/morrow/voice/respond"), {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(token ? { "x-clerk-token": token } : {}),
          },
          body: JSON.stringify({
            persona: brain.persona,
            // A shorter prompt reaches the first word sooner.
            history: [
              ...brain.history(),
              // What was said this call that hasn't reached the chat yet.
              ...voiceLog.current.filter(
                (v) => !brain.history().some((m) => m.text.trim() === v.text.trim()),
              ),
            ]
              .slice(-8)
              .map((m) => ({ role: m.role, text: m.text.slice(0, 600) })),
            said,
            withAudio: true,
            speaker: propsRef.current.speaker,
          }),
          signal: run.ac.signal,
        });
        if (!res.ok || !res.body) throw new Error("voice brain unavailable");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let sep = buf.indexOf("\n\n");
          while (sep >= 0) {
            const line = buf.slice(0, sep).trim();
            buf = buf.slice(sep + 2);
            sep = buf.indexOf("\n\n");
            if (!line.startsWith("data:")) continue;
            let ev: { t?: string; d?: string } | null = null;
            try {
              ev = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }
            if (ev?.t === "text" && typeof ev.d === "string") run.text += ev.d;
            else if (ev?.t === "audio" && typeof ev.d === "string") onBrainAudio(run, ev.d);
            else if (ev?.t === "error") throw new Error("voice brain failed");
          }
        }
        if (!run.text.trim()) throw new Error("voice brain said nothing");
        run.done = true;
        finishBrain(run);
      })().catch(() => {
        if (run.ac.signal.aborted || brainRun.current !== run) return;
        // The fast brain is unavailable: let the agent take this turn instead.
        brainRun.current = null;
        if (!run.confirmed) return;
        awaitingReply.current = true;
        propsRef.current.onUtterance(said);
        scheduleFillers([1200, 15000, 35000], 0);
      });
    },
    [finishBrain, onBrainAudio, scheduleFillers],
  );

  // A new voice picked mid-conversation: re-record the fillers in it, and say a word so you hear it.
  const spokenWith = useRef(props.speaker);
  useEffect(() => {
    if (spokenWith.current === props.speaker) return;
    spokenWith.current = props.speaker;
    fillers.current = [];
    if (status === "idle" || status === "error" || !player.current) return;
    loadFillers();
    if (status !== "listening") return;
    queue.current.push(fetchSpeech("Okay, this is how I'll sound."));
    void drainRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.speaker]);

  // Speak the reply as it streams: complete sentences now, the remainder when it's done.
  useEffect(() => {
    const reply = props.reply;
    if (!reply || status === "idle" || status === "error") return;
    if (spoken.current.id !== reply.id) spoken.current = { id: reply.id, upTo: 0 };
    const { chunk, next } = takeSentences(reply.text, spoken.current.upTo, reply.done);
    if (chunk.trim()) {
      spoken.current.upTo = next;
      const say = speakable(chunk);
      if (say) {
        awaitingReply.current = false;
        clearFillers();
        queue.current.push(fetchSpeech(say));
        void drain();
      }
    }
    if (reply.done) awaitingReply.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.reply?.id, props.reply?.text, props.reply?.done]);

  // Once the turn we triggered has started and finished, stop waiting — even if it said nothing
  // (e.g. it only took tool steps) — and go back to listening when nothing is left to play.
  const sawBusy = useRef(false);
  useEffect(() => {
    if (props.busy) {
      sawBusy.current = true;
      return;
    }
    if (!sawBusy.current) return;
    sawBusy.current = false;
    awaitingReply.current = false;
    if (status === "thinking" && queue.current.length === 0 && !draining.current) {
      setStatus("listening");
    }
  }, [props.busy, status]);

  const stop = useCallback(() => {
    stopSpeaking();
    ws.current?.close();
    ws.current = null;
    for (const t of stream.current?.getTracks() ?? []) t.stop();
    stream.current = null;
    void ctx.current?.close().catch(() => undefined);
    ctx.current = null;
    player.current?.close();
    player.current = null;
    setCaption("");
    setLevel(0);
    inputLevel.current = 0;
    setStatus("idle");
  }, [stopSpeaking]);

  const start = useCallback(async () => {
    setError(null);
    setStatus("connecting");
    try {
      const token = await getClerkToken();
      if (!token) throw new Error("Sign in to use voice.");
      const mic = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      stream.current = mic;
      if (!player.current) player.current = new PcmPlayer();
      player.current.resume();
      const audio = new AudioContext();
      ctx.current = audio;
      const blobUrl = URL.createObjectURL(new Blob([PCM_WORKLET], { type: "text/javascript" }));
      await audio.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);
      const node = new AudioWorkletNode(audio, "pcm16k");
      audio.createMediaStreamSource(mic).connect(node);

      if (fillers.current.length === 0) loadFillers();
      let heardWords = "";
      // Latest live transcript of your current turn, and how long the mic has been quiet.
      let latestWords = "";
      let quietFrames = 0;
      // Audio captured while the recogniser is (re)connecting — sent as soon as it's open, so
      // words spoken the moment you tap Talk (or during a reconnect) aren't lost.
      const early: ArrayBuffer[] = [];
      let retries = 0;
      const connect = async () => {
        const fresh = retries === 0 ? token : ((await getClerkToken().catch(() => null)) ?? token);
        if (stream.current !== mic) return; // ended meanwhile
        const sock = new WebSocket(listenUrl(fresh, propsRef.current.name));
        sock.binaryType = "arraybuffer";
        ws.current = sock;
        sock.onopen = () => {
          retries = 0;
          setError(null);
          setStatus((now) => (now === "connecting" ? "listening" : now));
          for (const chunk of early.splice(0)) sock.send(chunk);
        };
        sock.onclose = (e) => {
          if (ws.current !== sock) return; // we closed it ourselves
          if (e.code === 1008 || e.code === 4401) {
            setError("Voice isn't available for this account.");
            stop();
            setStatus("error");
            return;
          }
          // The recogniser connection dropped: reconnect quietly (the mic keeps running).
          if (retries < 4) {
            retries += 1;
            heardWords = "";
            latestWords = "";
            setTimeout(() => void connect(), 250 * retries);
            return;
          }
          setError("Voice disconnected. Check your connection and try again.");
          stop();
          setStatus("error");
        };
        sock.onmessage = onMessage;
      };
      const onMessage = (e: MessageEvent) => {
        if (typeof e.data !== "string") return;
        let msg: { event?: string; transcript?: string } | null = null;
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        const words = (msg?.transcript ?? "").trim();
        if (msg?.event === "StartOfTurn") heardWords = "";
        if (msg?.event === "Update" || msg?.event === "StartOfTurn") latestWords = words;
        if (msg?.event === "EndOfTurn") latestWords = "";
        if (
          msg?.event === "Update" ||
          msg?.event === "StartOfTurn" ||
          msg?.event === "TurnResumed"
        ) {
          setCaption(words);
          // Barge-in: real words while it's speaking or working → stop it and listen.
          if (words.length >= 3 && words !== heardWords) {
            heardWords = words;
            const run = brainRun.current;
            const brainTalking = !!run?.confirmed && !run.done;
            const speaking = player.current?.busy || queue.current.length > 0;
            // Our own voice coming back through the speakers isn't you interrupting.
            const ownWords = `${run?.confirmed ? run.text : ""} ${propsRef.current.reply?.text ?? ""}`;
            if (
              (speaking || awaitingReply.current || brainTalking) &&
              !(speaking && isEcho(words, ownWords))
            ) {
              // A false start ("Nice." … and you kept going) isn't an exchange worth keeping.
              const continuing = run && normWords(words).startsWith(normWords(run.said));
              if (run && !continuing) commitRun(run);
              stopSpeaking();
              awaitingReply.current = false;
              if (propsRef.current.busy) propsRef.current.onInterrupt();
              setStatus("listening");
            }
          }
        }
        if (propsRef.current.brain) {
          if (msg?.event === "EagerEndOfTurn" && words) {
            // Keep a reply already being prepared for exactly these words (started on silence).
            const ready = brainRun.current;
            if (!ready || ready.confirmed || !finishesWords(ready.said, words))
              runBrain(words, false);
            // You've paused: if you don't carry on (and the recogniser hasn't called the end of
            // your turn first, which it usually does), take it as done after 0.6s and answer.
            // Shorter answered half-sentences like "Nice." while you were still going.
            const run = brainRun.current;
            if (eagerTimer.current) clearTimeout(eagerTimer.current);
            eagerTimer.current = setTimeout(() => {
              if (!run || brainRun.current !== run || run.confirmed) return;
              voiceLog.current.push({ role: "user", text: run.said });
              setCaption("");
              setStatus("thinking");
              confirmBrain(run);
            }, 600);
          }
          if (msg?.event === "TurnResumed" && brainRun.current && !brainRun.current.confirmed) {
            if (eagerTimer.current) clearTimeout(eagerTimer.current);
            brainRun.current.ac.abort();
            brainRun.current = null;
          }
          if (msg?.event === "EndOfTurn") {
            setCaption("");
            if (!words) return;
            if (eagerTimer.current) clearTimeout(eagerTimer.current);
            const run = brainRun.current;
            if (run && finishesWords(run.said, words)) {
              run.said = words;
              // Already answering this (the eager pause may have confirmed it) — keep that reply.
              if (!run.confirmed) {
                voiceLog.current.push({ role: "user", text: words });
                setStatus("thinking");
                confirmBrain(run);
              }
            } else {
              voiceLog.current.push({ role: "user", text: words });
              setStatus("thinking");
              runBrain(words, true);
            }
          }
          return;
        }
        if (msg?.event === "StartOfTurn" && pendingSay.current) {
          // You kept going: hold the earlier piece and join it to what comes next — but if that
          // turns out to be noise, still send it on its own after a few seconds.
          const held = pendingSay.current;
          clearTimeout(held.timer);
          held.timer = setTimeout(() => {
            if (pendingSay.current !== held) return;
            pendingSay.current = null;
            awaitingReply.current = true;
            propsRef.current.onUtterance(held.text);
          }, 6000);
        }
        if (msg?.event === "EndOfTurn") {
          setCaption("");
          if (!words) return;
          const text = pendingSay.current ? `${pendingSay.current.text} ${words}` : words;
          if (pendingSay.current) clearTimeout(pendingSay.current.timer);
          setStatus("thinking");
          pendingSay.current = {
            text,
            timer: setTimeout(() => {
              pendingSay.current = null;
              awaitingReply.current = true;
              propsRef.current.onUtterance(text);
              // Keep the wait alive: a quick acknowledgement, then the odd update while it works.
              scheduleFillers([1200, 15000, 35000], 0);
            }, 900),
          };
        }
      };
      node.port.onmessage = (e: MessageEvent<{ pcm: ArrayBuffer; level: number }>) => {
        inputLevel.current = mutedRef.current ? 0 : e.data.level;
        setLevel(inputLevel.current);
        if (mutedRef.current) return;
        quietFrames = e.data.level < QUIET_LEVEL ? quietFrames + 1 : 0;
        if (quietFrames === QUIET_FRAMES && latestWords && propsRef.current.brain) {
          const run = brainRun.current;
          const idle =
            !player.current?.busy && queue.current.length === 0 && !awaitingReply.current;
          // Never cut off a confirmed reply that's still on its way; a finished one is just history.
          const stale = !run || (run.confirmed ? run.done : !finishesWords(run.said, latestWords));
          if (idle && stale) {
            runBrain(latestWords, false);
          }
        }
        const sock = ws.current;
        if (sock?.readyState === WebSocket.OPEN) sock.send(e.data.pcm);
        else if (early.length < 100) early.push(e.data.pcm);
      };
      void connect();
    } catch (cause) {
      stop();
      setStatus("error");
      setError(
        cause instanceof DOMException && cause.name === "NotAllowedError"
          ? "Microphone access was blocked. Allow it in your browser to talk."
          : cause instanceof Error
            ? cause.message
            : "Couldn't start voice.",
      );
    }
  }, [stop, stopSpeaking, runBrain, confirmBrain, commitRun, scheduleFillers, loadFillers]);

  useEffect(() => () => stop(), [stop]);

  /** Live loudness of you (mic) and of it (speech), 0–1 — read every frame by the voice orb. */
  const levels = useCallback(
    () => ({ input: inputLevel.current, output: player.current?.level() ?? 0 }),
    [],
  );

  return {
    status,
    error,
    caption,
    level,
    levels,
    muted,
    setMuted,
    start,
    stop,
    active: status !== "idle",
  };
}
